import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.find.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.sort.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.regexp.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import getLocalisedDate from 'utils/dates/getLocalisedDate';
import { isHelixOnlyHub, getHelixGraphTooltip, getHelixUIMode } from 'utils/helix';
import { useFetchUserAnalysis, useFetchUserAnalysisSkills } from 'actions/users';
import { fetchUserAnalysis } from 'actions/users/useFetchUserAnalysis';
import usersKeys from 'actions/users/queryKeys';
import { fetchUserAnalysisSkills } from 'actions/users/useFetchUserAnalysisSkills';
import EmptyStateController from 'components/EmptyStateController';
import Stack from 'components/Stack';
import GraphCard from 'components/GraphCard';
import CollectionGrid, { CollectionGridItem } from 'components/CollectionGrid';
import VisuallyHidden from 'components/VisuallyHidden';
import Landmark from 'components/Landmark';
import Text from 'components/Text';
import Heading from 'components/Heading';
import BarChartCard from 'components/BarChartCard';
import SkillPageWrapper from '../components/SkillPageWrapper';
import MeetingSection from '../components/MeetingSection';
import SummaryFlagCard from '../components/SummaryFlagCard';
import Box from 'components/Box';
import HeadingWithFilter from '../components/HeadingWithFilter';
import NoTrackedMeetingsEmptyContent from '../components/NoTrackedMeetingsEmptyContent';
import SkillScoreFlagCard from '../components/SkillScoreFlagCard';
import NoSkillsNote from './NoSkillsNote';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function WeekAnalysisPage(_ref) {
  var _skillsData$skills7;
  var isCurrentUser = _ref.isCurrentUser,
    weeksLoading = _ref.weeksLoading,
    weeksError = _ref.weeksError,
    userId = _ref.userId,
    user = _ref.user,
    weekCommencing = _ref.weekCommencing,
    _ref$weeks = _ref.weeks,
    weeks = _ref$weeks === void 0 ? [] : _ref$weeks,
    getUserSkillLocation = _ref.getUserSkillLocation,
    setWeekCommencing = _ref.setWeekCommencing;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var history = useHistory();
  var queryClient = useQueryClient();
  var _getHelixUIMode = getHelixUIMode(),
    showFeedback = _getHelixUIMode.showFeedback,
    showScores = _getHelixUIMode.showScores;
  var _useFetchUserAnalysis = useFetchUserAnalysis({
      userId: userId,
      weekCommencingDate: weekCommencing
    }),
    analysis = _useFetchUserAnalysis.data,
    analysisLoading = _useFetchUserAnalysis.isLoading,
    analysisError = _useFetchUserAnalysis.isError;
  var weekCommencingDate = (weekCommencing === null || weekCommencing === void 0 ? void 0 : weekCommencing.toString()) || weeks[0];
  var _useFetchUserAnalysis2 = useFetchUserAnalysisSkills({
      userId: userId,
      weekCommencingDate: (weekCommencing === null || weekCommencing === void 0 ? void 0 : weekCommencing.toString()) || weeks[0]
    }),
    skillsData = _useFetchUserAnalysis2.data,
    skillsLoading = _useFetchUserAnalysis2.isLoading,
    skillsError = _useFetchUserAnalysis2.isError;

  // Prefetch analysis date for other dates in background, skipping already cached ones
  useEffect(function () {
    var _skillsData$dates;
    skillsData === null || skillsData === void 0 || (_skillsData$dates = skillsData.dates) === null || _skillsData$dates === void 0 || _skillsData$dates.forEach(function (date) {
      var prefetchUserAnalysisSkillsKey = usersKeys.singleUserAnalysisSkills({
        userId: userId,
        weekCommencingDate: date
      });
      if (!queryClient.getQueryData(prefetchUserAnalysisSkillsKey)) {
        queryClient.prefetchQuery({
          queryKey: prefetchUserAnalysisSkillsKey,
          queryFn: function queryFn() {
            return fetchUserAnalysisSkills({
              userId: userId,
              weekCommencingDate: date
            });
          }
        });
      }
      var prefetchUserAnalysisKey = usersKeys.singleUserAnalysis(userId, date);
      if (!queryClient.getQueryData(prefetchUserAnalysisKey)) {
        queryClient.prefetchQuery({
          queryKey: prefetchUserAnalysisKey,
          queryFn: function queryFn() {
            return fetchUserAnalysis({
              weekCommencingDate: date,
              userId: userId
            });
          }
        });
      }
    });
  }, [queryClient, skillsData === null || skillsData === void 0 ? void 0 : skillsData.dates, userId, weekCommencingDate]);
  var isLoading = weeksLoading || analysisLoading || skillsLoading;
  var isError = weeksError || analysisError || skillsError;
  var isEmpty = weeks.length === 0;
  var selected = weekCommencing || weeks[0];
  var chartLabels = useMemo(function () {
    var _skillsData$dates2;
    return (skillsData === null || skillsData === void 0 || (_skillsData$dates2 = skillsData.dates) === null || _skillsData$dates2 === void 0 ? void 0 : _skillsData$dates2.map(function (day) {
      return getLocalisedDate({
        date: day,
        options: {
          month: 'short',
          day: 'numeric'
        }
      }) || '';
    })) || [];
  }, [skillsData === null || skillsData === void 0 ? void 0 : skillsData.dates]);
  var chartLabelClicks = useMemo(function () {
    var _skillsData$dates3;
    return (skillsData === null || skillsData === void 0 || (_skillsData$dates3 = skillsData.dates) === null || _skillsData$dates3 === void 0 ? void 0 : _skillsData$dates3.map(function (day) {
      return function () {
        return setWeekCommencing(day);
      };
    })) || [];
  }, [skillsData === null || skillsData === void 0 ? void 0 : skillsData.dates, setWeekCommencing]);
  var hasSkillsDataForAGraph = useMemo(function () {
    var _skillsData$skills;
    return (skillsData === null || skillsData === void 0 || (_skillsData$skills = skillsData.skills) === null || _skillsData$skills === void 0 ? void 0 : _skillsData$skills.some(function (skill) {
      if (!(skill !== null && skill !== void 0 && skill.scoreEachWeek)) return false;
      var filteredScores = skill.scoreEachWeek.filter(function (item) {
        return item !== null;
      });
      return filteredScores.length >= 2;
    })) || false;
  }, [skillsData === null || skillsData === void 0 ? void 0 : skillsData.skills]);
  var barChartData = useMemo(function () {
    var _skillsData$skills2;
    return (skillsData === null || skillsData === void 0 || (_skillsData$skills2 = skillsData.skills) === null || _skillsData$skills2 === void 0 ? void 0 : _skillsData$skills2.map(function (skill) {
      return skill.score;
    })) || [];
  }, [skillsData === null || skillsData === void 0 ? void 0 : skillsData.skills]);
  var barChartLabels = useMemo(function () {
    var _skillsData$skills3;
    return (skillsData === null || skillsData === void 0 || (_skillsData$skills3 = skillsData.skills) === null || _skillsData$skills3 === void 0 ? void 0 : _skillsData$skills3.map(function (skill) {
      return skill.name;
    })) || [];
  }, [skillsData === null || skillsData === void 0 ? void 0 : skillsData.skills]);
  var formattedSelected = useMemo(function () {
    return getLocalisedDate({
      date: selected,
      options: {
        month: 'short',
        day: 'numeric'
      }
    });
  }, [selected]);
  var barChartTitleId = 'skills-this-week-heading';
  var hasSkills = skillsData.skills && skillsData.skills.length > 0;
  var skillsRecorded = useMemo(function () {
    var _skillsData$skills4;
    return showScores ? [] : skillsData === null || skillsData === void 0 || (_skillsData$skills4 = skillsData.skills) === null || _skillsData$skills4 === void 0 || (_skillsData$skills4 = _skillsData$skills4.filter(function (skill) {
      return skill.id;
    }) // Has an analysis record associated
    ) === null || _skillsData$skills4 === void 0 ? void 0 : _skillsData$skills4.sort(function (a, b) {
      return a.name > b.name ? 1 : -1;
    });
  }, [showScores, skillsData]);
  var skillsNotRecorded = useMemo(function () {
    var _skillsData$skills5;
    return showScores ? [] : skillsData === null || skillsData === void 0 || (_skillsData$skills5 = skillsData.skills) === null || _skillsData$skills5 === void 0 || (_skillsData$skills5 = _skillsData$skills5.filter(function (skill) {
      return !skill.id;
    }) // Has no analysis record associated
    ) === null || _skillsData$skills5 === void 0 ? void 0 : _skillsData$skills5.sort(function (a, b) {
      return a.name > b.name ? 1 : -1;
    });
  }, [showScores, skillsData]);
  return /*#__PURE__*/_jsxs(SkillPageWrapper, {
    user: user,
    children: [/*#__PURE__*/_jsx(Box, {
      mt: isHelixOnlyHub ? 'm' : '0',
      children: /*#__PURE__*/_jsx(HeadingWithFilter, {
        weeks: weeks,
        selected: selected,
        setWeekCommencing: setWeekCommencing,
        title: t('property_week_overview')
      })
    }), /*#__PURE__*/_jsx(EmptyStateController, {
      isLoading: isLoading,
      isError: isError,
      isEmpty: isEmpty,
      emptyContent: /*#__PURE__*/_jsx(NoTrackedMeetingsEmptyContent, {
        isCurrentUser: isCurrentUser
      }),
      children: /*#__PURE__*/_jsxs(Stack, {
        spacing: "l",
        children: [/*#__PURE__*/_jsxs("section", {
          "aria-labelledby": "skills-overview-heading",
          children: [/*#__PURE__*/_jsx(VisuallyHidden, {
            id: "skills-overview-heading",
            children: t('skills_title_overview')
          }), /*#__PURE__*/_jsxs(Stack, {
            spacing: "l",
            children: [!hasSkills && /*#__PURE__*/_jsx(NoSkillsNote, {}), showFeedback && /*#__PURE__*/_jsxs(Box, {
              maxWidth: 860,
              children: [(analysis === null || analysis === void 0 ? void 0 : analysis.summary) && /*#__PURE__*/_jsx(SummaryFlagCard, {
                summary: analysis.summary
              }), !(analysis !== null && analysis !== void 0 && analysis.summary) && !hasSkills && /*#__PURE__*/_jsx(Landmark, {
                children: /*#__PURE__*/_jsx(Text, {
                  dimmed: true,
                  children: t('skills_empty_state_data_processing')
                })
              })]
            }), hasSkills && showScores && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(Heading, {
                as: "h3",
                size: "small",
                mb: "s",
                id: barChartTitleId,
                children: t('skills_title_skills_this_week')
              }), /*#__PURE__*/_jsx(BarChartCard, {
                titleId: barChartTitleId,
                ariaLabel: t('skills_this_week_aria_label'),
                nullLabel: t('skills_no_score'),
                data: barChartData,
                labels: barChartLabels,
                onXLabelClick: function onXLabelClick(label) {
                  var _skillsData$skills6;
                  var skill = skillsData === null || skillsData === void 0 || (_skillsData$skills6 = skillsData.skills) === null || _skillsData$skills6 === void 0 ? void 0 : _skillsData$skills6.find(function (skill) {
                    return skill.name === label;
                  });
                  if (skill) {
                    var skillId = skill.skillId;
                    history.push(getUserSkillLocation({
                      skillId: skillId
                    }));
                  }
                }
              })]
            }), hasSkills && showScores && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(Heading, {
                as: "h3",
                size: "small",
                mb: "s",
                children: t('skills_title_skills_history')
              }), /*#__PURE__*/_jsx(CollectionGrid, {
                children: skillsData === null || skillsData === void 0 || (_skillsData$skills7 = skillsData.skills) === null || _skillsData$skills7 === void 0 ? void 0 : _skillsData$skills7.map(function (skill) {
                  var skillId = skill.skillId,
                    name = skill.name,
                    score = skill.score,
                    scoreEachWeek = skill.scoreEachWeek,
                    progress = skill.progress;
                  return /*#__PURE__*/_jsx(CollectionGridItem, {
                    children: /*#__PURE__*/_jsx(GraphCard, {
                      emptyMessage: t('empty_skill_usage_not_recorded_in_period'),
                      showGraph: hasSkillsDataForAGraph,
                      to: getUserSkillLocation({
                        skillId: skillId
                      }),
                      title: name,
                      score: score,
                      change: progress,
                      chartLabels: chartLabels,
                      chartLabelClicks: chartLabelClicks,
                      chartData: scoreEachWeek,
                      getTooltipReadout: getHelixGraphTooltip,
                      selectedValue: formattedSelected
                    })
                  }, skillId);
                })
              })]
            }), hasSkills && !showScores && /*#__PURE__*/_jsxs(Stack, {
              spacing: "l",
              maxWidth: 860,
              as: "list",
              children: [skillsRecorded === null || skillsRecorded === void 0 ? void 0 : skillsRecorded.map(function (skill) {
                var skillId = String(skill.skillId);
                return /*#__PURE__*/_jsx(SkillScoreFlagCard, {
                  canShowButtonRow: false,
                  skill: skill,
                  getLink: getUserSkillLocation
                }, "week-skill-recorded-".concat(skillId));
              }), skillsNotRecorded === null || skillsNotRecorded === void 0 ? void 0 : skillsNotRecorded.map(function (skill) {
                var skillId = String(skill.skillId);
                return /*#__PURE__*/_jsx(SkillScoreFlagCard, {
                  canShowButtonRow: false,
                  skill: skill,
                  getLink: getUserSkillLocation
                }, "week-skill-not-recorded".concat(skillId));
              })]
            })]
          })]
        }), /*#__PURE__*/_jsx(MeetingSection, {
          userMeetings: analysis === null || analysis === void 0 ? void 0 : analysis.userMeetings,
          getLocation: getUserSkillLocation
        })]
      })
    })]
  });
}
export default WeekAnalysisPage;